// src/components/Dashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, deleteDoc, doc, query, where, orderBy, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import ModelForm from './ModelForm';
import AnchorsManager from './anchors/AnchorsManager';
import { motion, AnimatePresence } from 'framer-motion';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './dashboard.css';

const Dashboard = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [showModelList, setShowModelList] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showAnchors, setShowAnchors] = useState(false);

  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  /* ------------------------ Utils ------------------------ */
  const safeConvertDate = (firebaseDate) => {
    try {
      if (!firebaseDate) return new Date();
      if (firebaseDate instanceof Date) return firebaseDate;
      if (typeof firebaseDate?.toDate === 'function') return firebaseDate.toDate();
      if (typeof firebaseDate === 'string') {
        const date = new Date(firebaseDate);
        if (!isNaN(date.getTime())) return date;
      }
      return new Date();
    } catch {
      return new Date();
    }
  };

  const formatDate = (date) => {
    try {
      const d = safeConvertDate(date);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      artifact: 'bi-box-seam',
      sculpture: 'bi-person-bounding-box',
      tool: 'bi-tools',
      weapon: 'bi-shield',
      jewelry: 'bi-gem',
      other: 'bi-three-dots'
    };
    return icons[category] || icons.other;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      artifact: 'Artefacto',
      sculpture: 'Escultura',
      tool: 'Herramienta',
      weapon: 'Arma',
      jewelry: 'Joyería',
      other: 'Otro'
    };
    return labels[category] || 'Otro';
  };

  /* ------------------------ Data ------------------------ */
  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      if (!currentUser?.uid) {
        setModels([]);
        return;
      }

      const modelsRef = collection(db, 'models');
      let q = query(modelsRef, where('userId', '==', currentUser.uid));

      // Intentar ordenar por createdAt (si el índice existe)
      try {
        const orderedQuery = query(
          modelsRef,
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        await getDocs(orderedQuery); // test
        q = orderedQuery;
      } catch {
        // fallback sin ordenar en Firestore
      }

      const snap = await getDocs(q);
      const modelsData = snap.docs.map(d => {
        const data = d.data() || {};
        return {
          id: d.id,
          name: data.name || 'Sin nombre',
          description: data.description || '',
          category: data.category || 'other',
          era: data.era || '',
          location: data.location || '',
          thumbnailUrl: data.thumbnailUrl || null,
          modelUrl: data.modelUrl || null,
          userId: data.userId || currentUser.uid,
          createdAt: safeConvertDate(data.createdAt),
          fileSize: data.fileSize || null,
          fileType: data.fileType || null,
          lastUpdated: data.lastUpdated ? safeConvertDate(data.lastUpdated) : null,
          isPublic: typeof data.isPublic === 'boolean' ? data.isPublic : false
        };
      });

      // Fallback de orden local si no hubo orderBy server
      // @ts-ignore (prop interna opcional)
      if (!q._query?.orderBy?.length) {
        modelsData.sort((a, b) => b.createdAt - a.createdAt);
      }

      setModels(modelsData);
    } catch (error) {
      console.error('Error fetching models:', error);
      setModels([]);
      enqueueSnackbar('Error al cargar los modelos', {
        variant: 'error',
        action: (
          <button onClick={fetchModels} className="snackbar-retry-btn">
            Reintentar
          </button>
        )
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, enqueueSnackbar]);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  const filteredModels = useMemo(() => {
    if (!Array.isArray(models)) return [];
    if (!searchTerm) return models;
    const s = searchTerm.toLowerCase();
    return models.filter(m =>
      (m.name || '').toLowerCase().includes(s) ||
      (m.description || '').toLowerCase().includes(s) ||
      (m.category || '').toLowerCase().includes(s) ||
      (m.era || '').toLowerCase().includes(s) ||
      (m.location || '').toLowerCase().includes(s)
    );
  }, [models, searchTerm]);

  /* ------------------------ Actions ------------------------ */
  const handleDelete = async (modelId) => {
    if (!modelId) return;
    if (window.confirm('¿Eliminar este modelo?')) {
      try {
        await deleteDoc(doc(db, 'models', modelId));
        setModels(prev => prev.filter(m => m.id !== modelId));
        enqueueSnackbar('Modelo eliminado', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting:', error);
        enqueueSnackbar('No se pudo eliminar', { variant: 'error' });
      }
    }
  };

  const handleEdit = (model) => {
    if (!model?.id) return;
    setSelectedModel(model);
    setShowForm(true);
    setShowModelList(false);
    setShowAnchors(false);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedModel(null);
    setShowModelList(true);
    setShowAnchors(false);
    fetchModels();
  };

  const handleNewModelClick = () => {
    setShowForm(true);
    setSelectedModel(null);
    setShowModelList(false);
    setShowAnchors(false);
  };

  const openAnchors = (model) => {
    setSelectedModel(model);
    setShowForm(false);
    setShowModelList(false);
    setShowAnchors(true);
  };

  const toggleModelList = () => setShowModelList(v => !v);

  const handleTogglePublic = async (model) => {
    if (!model?.id) return;
    const newState = !model.isPublic;
    try {
      await updateDoc(doc(db, 'models', model.id), { isPublic: newState, lastUpdated: new Date() });
      setModels(prev => prev.map(m => m.id === model.id ? { ...m, isPublic: newState, lastUpdated: new Date() } : m));
      enqueueSnackbar(newState ? 'Modelo publicado' : 'Modelo despublicado', { variant: 'success' });
    } catch (error) {
      console.error('Error toggling publish:', error);
      enqueueSnackbar('No se pudo actualizar la visibilidad', { variant: 'error' });
    }
  };

  /* ------------------------ Render ------------------------ */
  return (
    <div className="dashboard">
      <div className="dashboard-container">
        {/* Header */}
        <header className="dashboard-header">
          <div className="hdr-left">
            <h1 className="dashboard-title">
              <i className="bi bi-collection"></i>
              Mis Modelos 3D
            </h1>
            <p className="dashboard-subtitle">
              {filteredModels.length} {filteredModels.length === 1 ? 'modelo' : 'modelos'} encontrados
            </p>
          </div>

          <div className="actions-container">
            <button
              type="button"
              className={`btn btn-ghost search-toggle-btn ${searchOpen ? 'active' : ''}`}
              onClick={() => setSearchOpen((v) => !v)}
              aria-label={searchOpen ? 'Cerrar búsqueda' : 'Buscar modelos'}
              aria-expanded={searchOpen}
            >
              <i className={`bi ${searchOpen ? 'bi-x-lg' : 'bi-search'}`} aria-hidden="true" />
            </button>

            <div className={`search-container ${searchOpen ? 'is-open' : ''}`}>
              <i className="bi bi-search search-icon" />
              <input
                type="text"
                placeholder="Buscar modelos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                aria-label="Buscar modelos"
                autoFocus={searchOpen}
              />
            </div>

            <button onClick={handleNewModelClick} className="btn btn-primary">
              <i className="bi bi-plus-lg" aria-hidden="true" /> <span className="btn-text">Nuevo</span>
            </button>
          </div>
        </header>

        {/* Panel de edición */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              className="form-container model-form-shell"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: .2 }}
            >
              <ModelForm
                model={selectedModel}
                onClose={handleCloseForm}
                editMode={!!selectedModel}
                refreshModels={fetchModels}
              />
              <div className="form-shell-footer">
                <button onClick={toggleModelList} className="btn btn-ghost">
                  <i className={`bi bi-chevron-${showModelList ? 'up' : 'down'}`} aria-hidden="true" />
                  {showModelList ? ' Ocultar modelos' : ' Mostrar modelos'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gestor de Anclas */}
        {showAnchors && selectedModel && (
          <AnchorsManager
            model={selectedModel}
            onClose={() => { setShowAnchors(false); setShowModelList(true); fetchModels(); }}
          />
        )}

        {/* Lista */}
        {loading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p className="loading-text">Cargando modelos...</p>
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? (
              <>
                <i className="bi bi-search empty-icon" />
                <h3 className="empty-title">Sin resultados</h3>
                <p className="empty-description">No hay modelos que coincidan con “{searchTerm}”.</p>
                <button onClick={() => setSearchTerm('')} className="btn btn-ghost">
                  Limpiar búsqueda
                </button>
              </>
            ) : (
              <>
                <i className="bi bi-collection empty-icon" />
                <h3 className="empty-title">No tienes modelos aún</h3>
                <p className="empty-description">Agregá tu primer modelo 3D.</p>
                <button onClick={handleNewModelClick} className="btn btn-primary">
                  <i className="bi bi-plus-lg" /> Crear Modelo
                </button>
              </>
            )}
          </div>
        ) : showModelList && (
          <>
            {/* Controles de vista */}
            <div className="view-controls">
              <div className="view-toggle" role="tablist" aria-label="Cambiar vista">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  title="Vista de cuadrícula"
                  aria-pressed={viewMode === 'grid'}
                >
                  <i className="bi bi-grid" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                  title="Vista de tabla"
                  aria-pressed={viewMode === 'table'}
                >
                  <i className="bi bi-list-ul" />
                </button>
              </div>
              <div className="results-count">
                Mostrando {filteredModels.length} de {models.length}
              </div>
            </div>

            {viewMode === 'grid' ? (
              <div className="models-grid">
                <AnimatePresence>
                  {filteredModels.map((m) => (
                    <motion.article
                      key={m.id}
                      className={`model-card ${m.isPublic ? 'is-public' : 'is-private'}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: .2 }}
                    >
                      {/* Imagen + overlay */}
                      <div className="model-thumb">
                        {m.thumbnailUrl ? (
                          <img
                            src={m.thumbnailUrl}
                            alt={m.name}
                            onError={(e) => { e.currentTarget.src = ''; }}
                          />
                        ) : (
                          <div className="thumb-placeholder">
                            <i className="bi bi-image" />
                          </div>
                        )}

                        {/* Gradiente para legibilidad de texto sobre imagen */}
                        <div className="thumb-gradient" />

                        {/* Acciones sobre la imagen (icon-only) */}
                        <div className="card-actions">
                          <button
                            className="icon-btn"
                            onClick={() => handleEdit(m)}
                            title="Editar"
                            aria-label="Editar"
                          >
                            <i className="bi bi-pencil-square" />
                          </button>
                          <button
                            className="icon-btn"
                            onClick={() => openAnchors(m)}
                            title="Accesorios & Anclas"
                            aria-label="Accesorios"
                          >
                            <i className="bi bi-pin-map-fill" />
                          </button>
                          <button
                            className="icon-btn"
                            onClick={() => handleTogglePublic(m)}
                            title={m.isPublic ? 'Despublicar' : 'Publicar'}
                            aria-label={m.isPublic ? 'Despublicar' : 'Publicar'}
                          >
                            <i className={`bi ${m.isPublic ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`} />
                          </button>
                          <button
                            className="icon-btn danger"
                            onClick={() => handleDelete(m.id)}
                            title="Eliminar"
                            aria-label="Eliminar"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>

                        {/* Badge estado */}
                        <div className={`publish-badge ${m.isPublic ? 'on' : 'off'}`}>
                          <i className={`bi ${m.isPublic ? 'bi-eye-fill' : 'bi-eye-slash-fill'}`} />
                          <span>{m.isPublic ? 'Publicado' : 'Oculto'}</span>
                        </div>

                        {/* Info sobre imagen */}
                        <div className="thumb-info">
                          <h3 className="model-name">{m.name}</h3>
                          <div className="meta-mini">
                            <span className="chip">
                              <i className={`bi ${getCategoryIcon(m.category)}`} />
                              {getCategoryLabel(m.category)}
                            </span>
                            <span className="chip">
                              <i className="bi bi-calendar" />
                              {formatDate(m.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="model-body">
                        {m.description && (
                          <p className="model-desc">{m.description}</p>
                        )}
                        <div className="model-meta-row">
                          {m.location && (
                            <span className="mini">
                              <i className="bi bi-geo-alt" />
                              {m.location}
                            </span>
                          )}
                          {m.era && (
                            <span className="mini">
                              <i className="bi bi-clock-history" />
                              {m.era}
                            </span>
                          )}
                          {m.fileSize && (
                            <span className="mini">
                              <i className="bi bi-file-earmark" />
                              {formatFileSize(m.fileSize)}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="models-table-container">
                <table className="models-table">
                  <thead>
                    <tr>
                      <th className="model-col">Modelo</th>
                      <th className="category-col"><i className="bi bi-tags" title="Categoría" /></th>
                      <th className="era-col"><i className="bi bi-clock-history" title="Época" /></th>
                      <th className="location-col"><i className="bi bi-geo-alt" title="Ubicación" /></th>
                      <th className="size-col"><i className="bi bi-file-earmark" title="Tamaño" /></th>
                      <th className="date-col"><i className="bi bi-calendar" title="Fecha" /></th>
                      <th className="status-col"><i className="bi bi-eye" title="Estado" /></th>
                      <th className="actions-col"><i className="bi bi-three-dots" title="Acciones" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence initial={false}>
                      {filteredModels.map((m) => (
                        <motion.tr
                          key={m.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: .15 }}
                          className={m.isPublic ? 'row-public' : 'row-private'}
                        >
                          <td className="model-col" data-label="Modelo">
                            <div className="model-info">
                              <div className="model-thumb-table">
                                {m.thumbnailUrl ? (
                                  <img
                                    src={m.thumbnailUrl}
                                    alt={m.name}
                                    onError={(e) => { e.currentTarget.src = ''; }}
                                  />
                                ) : (
                                  <div className="thumb-table-placeholder">
                                    <i className="bi bi-image" />
                                  </div>
                                )}
                              </div>
                              <div className="model-text-info">
                                <div className="model-name-table">{m.name}</div>
                                {m.description && <div className="model-description-table">{m.description}</div>}
                              </div>
                            </div>
                          </td>

                          <td className="category-col" data-label="Categoría">
                            <span className="mini">
                              <i className={`bi ${getCategoryIcon(m.category)}`} />
                              {getCategoryLabel(m.category)}
                            </span>
                          </td>

                          <td className="era-col" data-label="Época">{m.era || '-'}</td>
                          <td className="location-col" data-label="Ubicación">{m.location || '-'}</td>
                          <td className="size-col" data-label="Tamaño">{formatFileSize(m.fileSize)}</td>
                          <td className="date-col" data-label="Fecha">{formatDate(m.createdAt)}</td>

                          <td className="status-col" data-label="Estado">
                            <span className={`status-chip ${m.isPublic ? 'on' : 'off'}`}>
                              <i className={`bi ${m.isPublic ? 'bi-eye-fill' : 'bi-eye-slash-fill'}`} />
                              {m.isPublic ? 'Publicado' : 'Oculto'}
                            </span>
                          </td>

                          <td className="actions-col" data-label="Acciones">
                            <div className="table-actions">
                              <button
                                className="icon-btn"
                                onClick={() => handleEdit(m)}
                                title="Editar"
                                aria-label="Editar"
                              >
                                <i className="bi bi-pencil-square" />
                              </button>
                              <button
                                className="icon-btn"
                                onClick={() => openAnchors(m)}
                                title="Accesorios & Anclas"
                                aria-label="Accesorios"
                              >
                                <i className="bi bi-pin-map-fill" />
                              </button>
                              <button
                                className="icon-btn"
                                onClick={() => handleTogglePublic(m)}
                                title={m.isPublic ? 'Despublicar' : 'Publicar'}
                                aria-label={m.isPublic ? 'Despublicar' : 'Publicar'}
                              >
                                <i className={`bi ${m.isPublic ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`} />
                              </button>
                              <button
                                className="icon-btn danger"
                                onClick={() => handleDelete(m.id)}
                                title="Eliminar"
                                aria-label="Eliminar"
                              >
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
